import {
  StatsigProvider,
  type StatsigUser,
  useClientBootstrapInit,
  useStatsigClient as useStatsigClientLib,
} from '@statsig/react-bindings';
import { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { isBrowser } from '@/utils/isBrowser';

export const errorHandler = (err: Error, message: string) => {
  const msg = `FAE-894 graceful degradation of Statsig: ${message}`;

  if (isBrowser && window.NREUM) {
    window.NREUM?.noticeError?.(msg, {
      message: err.message,
      stack: err.stack,
    });
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      JSON.stringify({
        msg,
        err,
        message: err.message,
        stack: err.stack,
      }),
    );
  }
};

export type ContributorWebStatsigProviderProps = React.PropsWithChildren<{
  statsigProps: {
    user: StatsigUser;
    data: string;
    key: string;
  };
}>;

export const ContributorWebStatsigProvider = ({ statsigProps, children }: ContributorWebStatsigProviderProps) => {
  const previousDataRef = useRef(statsigProps.data);

  const statSigClient = useClientBootstrapInit(statsigProps.key, statsigProps.user, statsigProps.data, {
    networkConfig: {
      // This comes from the statsig proxy url found in project settings
      // https://console.statsig.com/2d7YKn3svx51kl9ppvADhm/api_keys
      api: 'https://wos7oui7lzrtk25y2ylbmzyasa0rqmwe.lambda-url.ap-south-1.on.aws/',
    },
  });

  // update the user when the data changes
  // we need to do this because we include data about the page and page type in the user
  if (previousDataRef.current !== statsigProps.data && statSigClient?.dataAdapter) {
    try {
      statSigClient.dataAdapter.setData(statsigProps.data);
      previousDataRef.current = statsigProps.data;
      statSigClient.updateUserSync(statsigProps.user);
    } catch (err) {
      errorHandler(err, 'Statsig client update failed');
    }
  }

  return (
    <ErrorBoundary
      onError={(err) => errorHandler(err, 'Statsig Provider error boundary')}
      FallbackComponent={() => null}
    >
      <StatsigProvider client={statSigClient}>{children}</StatsigProvider>
    </ErrorBoundary>
  );
};

export const useStatsigClient = () => useStatsigClientLib();
